/**
 * RigSentinel — Feature access (single source of truth).
 *
 * RC-1 Freemium model (2026-07):
 *   - Free:       signed-in without an active paid subscription; sees Converters
 *                 + 8 essential calculators; premium calculators, Professionalizer,
 *                 and Reports are locked.
 *   - Pro:        active `field_pro` subscription (or freemium overlay `pro`).
 *   - Enterprise: active `professional` subscription (or freemium overlay `enterprise`).
 *   - Owner:      hardcoded `rigsentinel@gmail.com` — bypasses ALL checks.
 *
 * Internal Paddle tier names (`field_pro`, `professional`) NEVER appear in
 * the UI. The freemium overlay fields (`isOwner`, `isPro`, `isEnterprise`,
 * `freemiumTier`) are the ONLY tier signal the UI should inspect.
 *
 * Fails CLOSED on entitlement load failure — premium features stay locked
 * if we cannot confirm the user has Pro access.
 */

// Legacy Paddle tier values that grant Pro access today. Kept for
// backwards-compatibility only; new code should use `ent.isPro`.
const PRO_TIERS = new Set(["field_pro", "professional"]);

// Status values that count as "currently entitled".
const ACTIVE_STATUSES = new Set(["active", "trialing", "past_due"]);

// The 8 free calculators (mapped to real catalog IDs in
// /app/frontend/src/lib/catalog.js). The /converters page is entirely free
// and is handled separately (route-level, not calculator-id-level).
export const FREE_CALCULATOR_IDS = [
  "hydrostatic_pressure",       // Basic Hydraulics
  "annular_velocity",           // Annular Velocity
  "pipe_capacity",              // Pipe Capacity
  "hole_volume",                // Volume Calculations
  "ecd",                        // ECD
  "pressure_gradient",          // Pressure Gradient
  "mud_weight_from_pressure",   // Mud Weight from Pressure and TVD
  "mud_cost_bbl",               // Mud Cost per Barrel
];

/** True iff the user has an active paid subscription (or is the owner). */
export function isProActive(ent) {
  if (!ent || ent.loading || ent.loadError) return false;
  // Owner overlay — hardcoded server-side + client-side.
  if (ent.isOwner) return true;
  // Freemium overlay — set by the backend for `field_pro` + `professional`.
  if (ent.isPro) return true;
  // Legacy path — kept as belt-and-suspenders.
  if (!ent.authenticated) return false;
  if (!PRO_TIERS.has(ent.tier)) return false;
  if (!ACTIVE_STATUSES.has(ent.status)) return false;
  return true;
}

/** True iff the user is the hardcoded owner (bypasses every restriction). */
export function isOwner(ent) {
  return Boolean(ent && ent.isOwner);
}

/** True iff the user is on the Enterprise plan (or is the owner). */
export function isEnterpriseActive(ent) {
  if (!ent || ent.loading || ent.loadError) return false;
  if (ent.isOwner) return true;
  return Boolean(ent.isEnterprise);
}

/** True iff `calcId` is a free calculator the user is entitled to run. */
export function isCalculatorAccessible(ent, calcId) {
  // Owner + Pro + Enterprise → full access.
  if (isProActive(ent)) return true;
  // Free tier → only the 8 free calculators are runnable.
  if (!calcId) return false;
  return FREE_CALCULATOR_IDS.includes(calcId);
}

/**
 * Coarse access record for a user. Mirrors the shape returned by the
 * backend `_freemium_overlay` helper.
 */
export function getFeatureAccess(ent) {
  if (isProActive(ent)) {
    return {
      canAccessAllCalculators: true,
      canAccessProfessionalizer: true,
      canAccessReports: true,
      canAccessConverters: true,
      canAccessEnterpriseDashboard: isEnterpriseActive(ent),
      freeCalculators: FREE_CALCULATOR_IDS,
      isOwner: isOwner(ent),
      tier: isEnterpriseActive(ent) ? "enterprise" : "pro",
    };
  }
  return {
    canAccessAllCalculators: false,
    canAccessProfessionalizer: false,
    canAccessReports: false,
    canAccessConverters: true,
    canAccessEnterpriseDashboard: false,
    freeCalculators: FREE_CALCULATOR_IDS,
    isOwner: false,
    tier: "free",
  };
}

/** True iff the user is signed in (regardless of paid status). */
export function isAuthenticated(ent) {
  return Boolean(ent && !ent.loading && ent.authenticated);
}

/** True iff the entitlement is still being fetched. */
export function isLoading(ent) {
  return Boolean(ent && ent.loading);
}

/** True iff the entitlement endpoint failed to load. */
export function isLoadError(ent) {
  return Boolean(ent && ent.loadError);
}

/**
 * Coarse access level — exactly one of "public", "preview", or "pro".
 * Used by routes / pages that branch on user state.
 */
export function getAccessLevel(ent) {
  if (isProActive(ent)) return "pro";
  if (isAuthenticated(ent)) return "preview";
  return "public";
}

/**
 * Canonical upgrade message. `context` lets callers tailor the message
 * to the action that was blocked (e.g. "polish report").
 */
export function getUpgradeMessage(context) {
  if (context) {
    return `${context} is available with RigSentinel Pro.`;
  }
  return "Unlock this with RigSentinel Pro.";
}

/**
 * Dropdown / header plan label.
 */
export function formatPlanLabel(ent) {
  if (!ent) return "Preview";
  if (ent.loadError) return "Unable to load";
  if (ent.isOwner) return "RigSentinel Owner";
  if (!isProActive(ent)) return "Preview";
  if (ent.isEnterprise || ent.tier === "professional") {
    if (ent.cadence === "monthly") return "RigSentinel Enterprise Monthly";
    if (ent.cadence === "annual") return "RigSentinel Enterprise Annual";
    return "RigSentinel Enterprise";
  }
  if (ent.cadence === "monthly") return "RigSentinel Pro Monthly";
  if (ent.cadence === "annual") return "RigSentinel Pro Annual";
  return "RigSentinel Pro";
}

export function formatStatusLabel(ent) {
  if (!ent) return null;
  if (ent.loadError) return null;
  if (!isAuthenticated(ent)) return null;
  if (ent.isOwner) return "Owner";
  if (!PRO_TIERS.has(ent.tier)) return null;
  return mapPaddleStatusLabel(ent.status, ent.scheduledChange);
}

/**
 * CENTRAL Paddle → customer-facing status mapping. All components must use
 * this (or formatStatusLabel) — never interpret raw Paddle states locally.
 *   trialing → Trial · active → Active · past_due → Past Due · paused → Paused
 *   scheduledChange.action==="cancel" → Scheduled for Cancellation
 *   canceled → Cancelled · expired → Expired
 */
export function mapPaddleStatusLabel(status, scheduledChange) {
  if (scheduledChange && scheduledChange.action === "cancel" &&
      (status === "active" || status === "trialing" || status === "past_due")) {
    return "Scheduled for Cancellation";
  }
  switch (status) {
    case "trialing": return "Trial";
    case "active":   return "Active";
    case "canceled":
    case "cancelled": return "Cancelled";
    case "past_due": return "Past Due";
    case "paused":   return "Paused";
    case "expired":  return "Expired";
    default:         return null;
  }
}

/**
 * Error type thrown by action-level guards (e.g. pdfExport, save-sheet).
 */
export class FeatureLockedError extends Error {
  constructor(message, options = {}) {
    super(message || "This feature requires RigSentinel Pro.");
    this.name = "FeatureLockedError";
    this.feature = options.feature || null;
  }
}
